import { useCallback, useEffect, useRef, useState } from "react";
import type RealtimeKitClient from "@cloudflare/realtimekit";
import RealtimeKitVideoBackgroundTransformer from "@cloudflare/realtimekit-virtual-background";
import { ChevronUp, Image as ImageIcon, Loader2, Sparkles, SwitchCamera, Upload, Wand2, X } from "lucide-react";
import { flipCamera, hasBothCameraSides, isMobileCameraBrowser } from "./cameraFacing";
import {
    applyBlurCameraBackground,
    applyCameraBackground,
    applyConstelaCameraBackground,
    applyUploadedCameraBackground,
    forceReapplyCameraBackground,
    isConstelaDefaultBackgroundUrl,
    loadVirtualBackgroundPreference,
    type VirtualBackgroundPreference,
} from "./virtualBackgroundPersist";

type BackgroundChoice = "none" | "blur" | "constela" | "custom";

const PANEL_COLLAPSED_KEY = "constela-camera-bg-panel-collapsed";

const CHOICE_LABEL: Record<BackgroundChoice, string> = {
    none: "Sem fundo",
    blur: "Desfoque",
    constela: "Constela",
    custom: "Minha imagem",
};

function readCollapsed(): boolean {
    try {
        return sessionStorage.getItem(PANEL_COLLAPSED_KEY) === "1";
    } catch {
        return false;
    }
}

function writeCollapsed(collapsed: boolean): void {
    try {
        sessionStorage.setItem(PANEL_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
        // storage pode estar bloqueado
    }
}

type CameraBackgroundPanelProps = {
    meeting: RealtimeKitClient | null;
    compact?: boolean;
};

function choiceFromPreference(preference: VirtualBackgroundPreference | null): BackgroundChoice {
    if (!preference || preference.mode === "none") {
        return "none";
    }
    if (preference.mode === "blur") {
        return "blur";
    }
    if (isConstelaDefaultBackgroundUrl(preference.imageUrl)) {
        return "constela";
    }
    return "custom";
}

const CameraBackgroundPanel: React.FC<CameraBackgroundPanelProps> = ({
    meeting,
    compact = false,
}) => {
    const fileRef = useRef<HTMLInputElement>(null);
    const [choice, setChoice] = useState<BackgroundChoice>(() =>
        choiceFromPreference(loadVirtualBackgroundPreference()),
    );
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hint, setHint] = useState<string | null>(null);
    const [collapsed, setCollapsed] = useState(readCollapsed);
    const isMobile = isMobileCameraBrowser();
    const supported =
        typeof window !== "undefined" && RealtimeKitVideoBackgroundTransformer.isSupported();

    const [canFlip, setCanFlip] = useState(false);
    const [flipping, setFlipping] = useState(false);

    useEffect(() => {
        setChoice(choiceFromPreference(loadVirtualBackgroundPreference()));
    }, [meeting]);

    useEffect(() => {
        let alive = true;
        if (!meeting || !isMobileCameraBrowser()) {
            setCanFlip(false);
            return;
        }
        void hasBothCameraSides(meeting).then((both) => {
            if (alive) setCanFlip(both);
        });
        return () => {
            alive = false;
        };
    }, [meeting]);

    const handleFlip = useCallback(async () => {
        if (!meeting || flipping) {
            return;
        }
        setFlipping(true);
        setError(null);
        try {
            const facing = await flipCamera(meeting);
            if (!facing) {
                setError("Não achei outra câmera neste aparelho.");
            } else if (!meeting.self?.videoEnabled) {
                setError(
                    facing === "environment"
                        ? "Câmera traseira escolhida. Ela abre quando você ligar a câmera."
                        : "Câmera frontal escolhida. Ela abre quando você ligar a câmera.",
                );
            }
        } catch {
            setError("Não foi possível trocar de câmera. Tente de novo.");
        } finally {
            setFlipping(false);
        }
    }, [flipping, meeting]);

    const run = useCallback(
        async (next: BackgroundChoice, file?: File) => {
            if (!meeting) {
                return;
            }
            setBusy(true);
            setError(null);
            setHint(null);
            try {
                if (next === "none") {
                    await applyCameraBackground(meeting, { mode: "none" });
                } else if (next === "blur") {
                    const result = await applyBlurCameraBackground(meeting);
                    if (result === "unsupported" || result === "failed") {
                        throw new Error("Não foi possível aplicar o desfoque neste navegador.");
                    }
                } else if (next === "constela") {
                    const result = await applyConstelaCameraBackground(meeting);
                    if (result === "unsupported" || result === "failed") {
                        throw new Error("Não foi possível aplicar o papel da Constela.");
                    }
                } else if (file) {
                    const { result, warning } = await applyUploadedCameraBackground(meeting, file);
                    if (result === "unsupported" || result === "failed") {
                        throw new Error("Não foi possível aplicar essa imagem.");
                    }
                    if (warning) {
                        setHint(warning);
                    }
                }
                setChoice(next);
                setCollapsed(true);
                writeCollapsed(true);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Não foi possível alterar o fundo.");
            } finally {
                setBusy(false);
            }
        },
        [meeting],
    );

    const handleForceReapply = useCallback(async () => {
        if (!meeting || busy) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const result = await forceReapplyCameraBackground(meeting);
            if (result === "unsupported" || result === "failed") {
                throw new Error("Não foi possível reaplicar o fundo.");
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Não foi possível reaplicar o fundo.");
        } finally {
            setBusy(false);
        }
    }, [busy, meeting]);

    if (!meeting) {
        return null;
    }

    const buttonClass = (active: boolean) =>
        `flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-medium transition-colors ${
            active
                ? "bg-blue-600 text-white"
                : "bg-slate-700/80 text-slate-200 hover:bg-slate-600"
        } ${busy ? "opacity-60 pointer-events-none" : ""}`;

    const toggleCollapsed = () => {
        const next = !collapsed;
        setCollapsed(next);
        writeCollapsed(next);
    };

    return (
        <section
            className={`border-t border-gray-700/60 bg-slate-900/90 ${
                compact ? "py-1.5 pl-2 pr-[4.75rem]" : "px-3 py-2"
            }`}
            aria-label="Fundo da câmera"
        >
            <div className={`flex items-center gap-2 ${collapsed ? "" : "mb-1.5"}`}>
                <button
                    type="button"
                    onClick={toggleCollapsed}
                    className="flex shrink-0 touch-manipulation items-center gap-1 rounded-md bg-slate-700/90 px-2 py-1 text-[11px] font-semibold text-white hover:bg-slate-600"
                    aria-expanded={!collapsed}
                    aria-label={collapsed ? "Mostrar fundo da câmera" : "Fechar fundo da câmera"}
                >
                    {collapsed ? (
                        <>
                            <ChevronUp className="h-3.5 w-3.5" />
                            Abrir
                        </>
                    ) : (
                        <>
                            <X className="h-3.5 w-3.5" />
                            Fechar
                        </>
                    )}
                </button>
                <button
                    type="button"
                    onClick={toggleCollapsed}
                    className="min-w-0 flex-1 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-300"
                >
                    Fundo da câmera
                    {collapsed ? (
                        <span className="ml-1.5 font-medium normal-case tracking-normal text-slate-400">
                            · {CHOICE_LABEL[choice]}
                        </span>
                    ) : null}
                </button>
                <div className="flex shrink-0 items-center gap-1">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-400" /> : null}
                    {canFlip ? (
                        <button
                            type="button"
                            onClick={() => void handleFlip()}
                            disabled={flipping}
                            className="flex touch-manipulation items-center gap-1 rounded-md bg-slate-700/80 px-2 py-1 text-[11px] font-medium text-slate-100 hover:bg-slate-600 disabled:opacity-60"
                            aria-label="Virar câmera"
                        >
                            {flipping ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <SwitchCamera className="h-3.5 w-3.5" />
                            )}
                            Virar câmera
                        </button>
                    ) : null}
                </div>
            </div>
            {collapsed && error ? (
                <p className="mt-1 text-[11px] leading-snug text-amber-200">{error}</p>
            ) : null}
            {!collapsed &&
                (!supported ? (
                <p className="text-[11px] leading-snug text-amber-200">
                    {isMobile
                        ? "Fundo virtual exige Chrome ou Edge atualizado neste aparelho."
                        : "Este navegador não aplica fundo virtual. Use Chrome ou Edge no computador."}
                </p>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-1.5">
                        <button
                            type="button"
                            className={buttonClass(choice === "none")}
                            onClick={() => void run("none")}
                            aria-pressed={choice === "none"}
                        >
                            <X className="h-3.5 w-3.5" />
                            Sem fundo
                        </button>
                        <button
                            type="button"
                            className={buttonClass(choice === "blur")}
                            onClick={() => void run("blur")}
                            aria-pressed={choice === "blur"}
                        >
                            <Wand2 className="h-3.5 w-3.5" />
                            Desfoque
                        </button>
                        <button
                            type="button"
                            className={buttonClass(choice === "constela")}
                            onClick={() => void run("constela")}
                            aria-pressed={choice === "constela"}
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            Constela
                        </button>
                        <button
                            type="button"
                            className={buttonClass(choice === "custom")}
                            onClick={() => {
                                setHint(
                                    isMobile
                                        ? "Use luz na frente do rosto e evite fundos claros no centro da foto."
                                        : null,
                                );
                                fileRef.current?.click();
                            }}
                            aria-pressed={choice === "custom"}
                        >
                            <Upload className="h-3.5 w-3.5" />
                            Minha imagem
                        </button>
                    </div>
                    {choice !== "none" ? (
                        <button
                            type="button"
                            onClick={() => void handleForceReapply()}
                            disabled={busy}
                            className="mt-1.5 w-full rounded-lg bg-slate-700/80 px-2 py-1.5 text-[11px] font-medium text-slate-100 hover:bg-slate-600 disabled:opacity-60"
                        >
                            Rosto cortado? Reaplicar fundo
                        </button>
                    ) : null}
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        aria-label="Enviar imagem de fundo da câmera"
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            if (file) {
                                void run("custom", file);
                            }
                        }}
                    />
                    {error ? (
                        <p className="mt-1.5 text-[11px] leading-snug text-red-300">{error}</p>
                    ) : hint ? (
                        <p className="mt-1.5 text-[11px] leading-snug text-amber-200">{hint}</p>
                    ) : (
                        <p className="mt-1.5 flex items-start gap-1 text-[10px] leading-snug text-slate-400">
                            <ImageIcon className="mt-0.5 h-3 w-3 shrink-0" />
                            {isMobile
                                ? "O fundo fica salvo neste aparelho e volta sozinho se a câmera reiniciar."
                                : "O fundo fica salvo neste computador e volta sozinho se a câmera reiniciar."}
                        </p>
                    )}
                </>
                ))}
        </section>
    );
};

export default CameraBackgroundPanel;
